import { getFormErrorMessage } from "@core/utility/get-error-message";
import { BirthdayRewardFormRule, BirthdayRewardFormSchema } from "@factory/form/form.rule";
import { useFormDefaultBirthdayReward } from "@factory/form/form-default-birthday-reward.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseBirthdayRewardModel } from "@factory/model/query-model-birthday-reward";
import { 
  useQyDeleteBirthdayReward, 
  useQyGetBirthdayRewardById, 
  useQyUpdateBirthdayReward} from "@factory/query/birthday-reward.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditBirthdayReward() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultBirthdayReward();
  const navigate = useNavigate();
  const { mapUpdateBirthdayReward, mapDeleteBirthdayReward } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { birthdayRewardId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID BirthdayReward
  const handleGetApiSuccess = (data: GetResponseBirthdayRewardModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("tenant", responseData?.tenant || "");
      setValue("points", responseData?.points);
      setValue("stamp", responseData?.stamp);
      setValue("expiryDays", responseData?.expiry_days);
  };
  const {
    data: birthdayRewardResponse,
    isLoading,
    isError: birthdayRewardError,
  } = useQyGetBirthdayRewardById(birthdayRewardId || "", handleGetApiSuccess);
  const birthdayRewardData = birthdayRewardResponse?.data?.[0];

  // API UPDATE BirthdayReward
  const handleUpdateApiSuccess = () => {
    showSuccess("BirthdayReward updated successfully");
  };
  const { mutate: updateBirthdayReward, isLoading: isEditLoading } = useQyUpdateBirthdayReward(
    handleUpdateApiSuccess
  );

  // API DELETE BirthdayReward
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("BirthdayReward deleted successfully");
  };
  const { mutate: deleteBirthdayReward } = useQyDeleteBirthdayReward(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<BirthdayRewardFormSchema>({
    defaultValues: getDefault(birthdayRewardData),
    resolver: zodResolver(BirthdayRewardFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: BirthdayRewardFormSchema) => {
    if (!birthdayRewardData || !birthdayRewardData.code) {
      throw new Error("No birthdayReward data");
    }

    const formData = mapUpdateBirthdayReward(form, birthdayRewardData.code);
    updateBirthdayReward(formData);
  };
  const handleValidateError = (err: FieldErrors<BirthdayRewardFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!birthdayRewardData || !birthdayRewardData.code) {
      throw new Error("No birthdayReward data found");
    }

    const form = mapDeleteBirthdayReward(birthdayRewardData.code);
    deleteBirthdayReward(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    birthdayRewardId,
    birthdayRewardData,
    isLoading,
    birthdayRewardError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}